/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/coldefs/metadata/datetime
 * @desc Column Definition for metadata datetime
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { isValid, parse, parseISO } from 'date-fns';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import { FormShowEditDateToggleComponent } from 'labstep-web/core/Form/ShowEdit/Date/ToggleComponent';
import type { IFormShowEditDateToggleComponentProps } from 'labstep-web/core/Form/ShowEdit/Date/ToggleComponent/types';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolCondition/components/ColumnHeader';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { Resource } from 'labstep-web/models/resource.model';
import {
  formatDateOnly,
  humanReadableDateAtUTC,
} from 'labstep-web/services/date.service';

const DATE_FORMAT = 'dd/MM/yyyy HH:mm';

export const getMetadataDatetimeColDef = (
  experimentOrProtocol: Experiment | Protocol,
  parentEntityClass: string,
  metadataType: MetadataType,
  metadataLabel: string,
  protocolValueName?: string,
): SmartTableColDef => {
  const setMetadataDate = (
    params: ValueSetterParams | CellRendererParams<Entity>,
    newValue: string | null,
  ) => {
    if (!params.node) {
      return;
    }

    const metadata =
      GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
        experimentOrProtocol,
        params.node.data,
        parentEntityClass,
        metadataLabel,
        metadataType,
        protocolValueName,
      ) ||
      GridSmartTableProtocolConditionService.getMetadataConstant(
        experimentOrProtocol,
        metadataLabel,
      );

    if (!metadata) {
      return GridSmartTableProtocolConditionService.createMetadata(
        params,
        experimentOrProtocol,
        parentEntityClass,
        metadataType,
        metadataLabel,
        protocolValueName,
        { date: newValue },
      );
    } else if (newValue === null) {
      return GridSmartTableProtocolConditionService.updateEntity(
        params,
        metadata.entityName,
        metadata.guid,
        {
          date: null,
        },
      );
    } else {
      return GridSmartTableProtocolConditionService.updateEntity(
        params,
        metadata.entityName,
        metadata.guid,
        {
          date: newValue,
        },
      );
    }
  };

  return {
    colId: protocolValueName
      ? `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}:${protocolValueName}`
      : `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridSmartTableColumnHeader,
    headerComponentParams: (params: HeaderComponentParamsProps) => {
      const variableTemplate =
        GridSmartTableProtocolConditionService.getMetadataVariableTemplate(
          experimentOrProtocol,
          metadataLabel,
        );

      const constant =
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      return {
        icon: protocolValueName
          ? ICONS.metadata.secondary
          : ICONS.metadata.type.date,
        cornerIcon:
          protocolValueName &&
          (parentEntityClass === Resource.entityName
            ? ICONS.resource.primary
            : ICONS.resource_item.primary),
        secondaryHeader:
          protocolValueName &&
          `${protocolValueName}${GridSmartTableProtocolConditionService.getMetadataParentPrefix(parentEntityClass)}`,
        toolPanelColumnName: `${GridSmartTableProtocolConditionService.getMetadataParentPrefix(parentEntityClass)}${metadataLabel}`,
        isConstant: !!constant,
        experimentOrProtocol,
        metadata: variableTemplate || constant,
      };
    },
    editable: (params: EditableCallbackParams) =>
      GridSmartTableProtocolConditionService.getMetadataEditable(
        params,
        experimentOrProtocol,
        metadataType,
        metadataLabel,
        parentEntityClass,
        protocolValueName,
      ),
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        ) ||
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      return metadata?.date;
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      if (params.newValue == null) {
        setMetadataDate(params, null);
        return true;
      }

      const dateISO = parseISO(params.newValue);
      if (isValid(dateISO)) {
        const newValue = formatDateOnly(dateISO);
        setMetadataDate(params, newValue);
        return true;
      }

      const date = parse(params.newValue, DATE_FORMAT, new Date());
      if (isValid(date)) {
        const newValue = formatDateOnly(date);
        setMetadataDate(params, newValue);
        return true;
      }

      return false;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        ) ||
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      if (!metadata || !metadata.date) {
        return (
          <DataGridPlaceholder
            params={params}
            editable={GridSmartTableProtocolConditionService.getMetadataEditable(
              params,
              experimentOrProtocol,
              metadataType,
              metadataLabel,
              parentEntityClass,
              protocolValueName,
            )}
            placeholder={
              GridSmartTableProtocolConditionService.getMetadataEditable(
                params,
                experimentOrProtocol,
                metadataType,
                metadataLabel,
                parentEntityClass,
                protocolValueName,
              )
                ? 'Enter'
                : ' '
            }
          />
        );
      }

      return <>{humanReadableDateAtUTC(metadata.date)}</>;
    },
    cellEditorPopup: true,
    cellEditorPopupPosition: 'under',
    cellEditor: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolConditionService.getProtocolConditionMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        ) ||
        GridSmartTableProtocolConditionService.getMetadataConstant(
          experimentOrProtocol,
          metadataLabel,
        );

      const onCloseFn: IFormShowEditDateToggleComponentProps['onClose'] =
        (date): void => {
          if (!date || date.length !== 1) {
            return;
          }

          const newValue = formatDateOnly(date[0]);
          if (metadata?.date !== newValue) {
            setMetadataDate(params, newValue);
          }
        };

      return (
        <FormShowEditDateToggleComponent
          canEdit
          dateTimePickerOnly
          viewComponent={<TextPlaceholder></TextPlaceholder>}
          defaultDate={metadata?.date || null}
          onClose={onCloseFn}
        />
      );
    },
    cellStyle: (params) =>
      GridSmartTableProtocolConditionService.getCellStyle(
        params,
        experimentOrProtocol,
        Metadata.entityName,
        metadataLabel,
        parentEntityClass,
      ),
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: () => null,
  };
};
